import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux-stores/store'
import { AppDispatch } from '../../redux-stores/store'
import { fetchReferrals } from '../../redux-stores/slices/referralSlice'
import { isNativePlatform } from '../../utils/platformUtil'
import { setHeader } from '../../redux-stores/slices/layoutSlice'
import { defaultAppHeaderProps } from '../../types/layout/layoutTypes'
import { toast } from 'react-toastify'
import { APP_NAME, WEB_APP_URL, APP_URL as referralLink } from '../../constants/constants'
import "./style.scss"
import referralRightGraphic from "../../assets/images/home/referral-right.png"
import referralLeftGraphic from "../../assets/images/home/referral-left.png"
import WhatsAppIcon from '../../assets/icons/WhatsAppIcon'
import LinkedInIcon from '../../assets/icons/LinkedInIcon'
import InstagramIcon from '../../assets/icons/InstagramIcon'
import XIcon from '../../assets/icons/XIcon'
import { formatAmount } from '../../utils/helpers'
import Loader from '../../components/loaders/Loader'
import ifConfigured from '../../utils/hoc/ifConfigured'

const Referral: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();

    const [completeReferralLink, setCompleteReferralLink] = useState<string>('');
    const [ready, setReady] = useState(false);

    const { items: referrals, program } = useSelector((state: RootState) => state.referrals);
    const currency = useSelector((state: RootState) => state.auth.user?.currency);
    const referralCode = useSelector((state: RootState) => state.auth.user?.referral_code);

    const copyURL = async () => {
        try {
            await navigator.clipboard.writeText(completeReferralLink);
            toast.success("Referral URL copied to your clipboard!");
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        Promise.resolve(dispatch(fetchReferrals(false))).finally(() => setReady(true));
    }, [dispatch])

    useEffect(() => {
        setCompleteReferralLink((isNativePlatform() ? WEB_APP_URL : referralLink) + '/register/?referrer=' + referralCode)
    }, [referralCode])

    useEffect(() => {
        return () => {
            dispatch(setHeader(defaultAppHeaderProps))
        }
    }, [])

    if (!ready) return <Loader isLoading />

    if (!program) {
        return (
            <div className='position-relative'>
                <img src={referralLeftGraphic} alt="referral-hands" className='referral-graphic left' />
                <img src={referralRightGraphic} alt="referral-hands" className='referral-graphic right' />
                <div className="referral-page-header"></div>
                <div className="referral-hero-section">
                    <h3 className="referral-unavailable-title">Referrals</h3>
                    <span className='referral-hero-subtitle'>Invite friends, help them find brand collabs, and earn rewards when a program is active.</span>
                </div>
                <div className="referral-link-card">
                    <div className="d-flex flex-column align-items-center justify-content-center text-center gap-2 py-4">
                        <span className='referral-empty-heading'>Referral program unavailable</span>
                        <span className='referral-empty-copy'>There is no active referral program at the moment. Please check back later.</span>
                    </div>
                </div>
            </div>
        )
    }

    const rewardAmount = formatAmount(program.reward_amount, currency?.code, currency?.symbol, currency?.locale)

    return (
        <div className='position-relative'>
            <img src={referralLeftGraphic} alt="referral-hands" className='referral-graphic left' />
            <img src={referralRightGraphic} alt="referral-hands" className='referral-graphic right' />
            <div className="referral-page-header"></div>
            <div className="referral-hero-section">
                <h3 className="referral-hero-title">
                    Earn {rewardAmount}
                </h3>
                <span className='referral-hero-subtitle'>Invite friends, help them find brand collabs, earn {rewardAmount}</span>
            </div>
            <div className="referral-link-card">
                <div className="d-flex flex-column gap-2">
                    <span className='referral-link-title'>Invite friends via link</span>
                    <div className='referral-link'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8.52912 10.5327L9.48284 9.57841C11.063 7.99725 11.063 5.43369 9.48284 3.85253C7.90267 2.27138 5.3407 2.27138 3.76053 3.85253L1.8531 5.76116C0.272926 7.34232 0.272926 9.90588 1.8531 11.487C2.58589 12.2203 3.52983 12.6135 4.48907 12.6667" stroke="black" strokeOpacity="0.7" strokeLinecap="round" />
                            <path d="M7.47088 5.46728L6.51716 6.42159C4.93699 8.00275 4.93699 10.5663 6.51716 12.1475C8.09733 13.7286 10.6593 13.7286 12.2395 12.1475L14.1469 10.2388C15.7271 8.65768 15.7271 6.09412 14.1469 4.51297C13.4141 3.77972 12.4702 3.3865 11.5109 3.33333" stroke="black" strokeOpacity="0.7" strokeLinecap="round" />
                        </svg>
                        <span>{referralCode ? completeReferralLink : ""}</span>
                    </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                    <button className='btn btn-rose' onClick={copyURL}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M4 7.33333C4 5.44772 4 4.50491 4.58579 3.91912C5.17157 3.33333 6.11438 3.33333 8 3.33333H10C11.8856 3.33333 12.8284 3.33333 13.4142 3.91912C14 4.50491 14 5.44772 14 7.33333V10.6667C14 12.5523 14 13.4951 13.4142 14.0809C12.8284 14.6667 11.8856 14.6667 10 14.6667H8C6.11438 14.6667 5.17157 14.6667 4.58579 14.0809C4 13.4951 4 12.5523 4 10.6667V7.33333Z" stroke="white" />
                            <path d="M4 12.6667C2.89543 12.6667 2 11.7712 2 10.6667V6.66667C2 4.15251 2 2.89543 2.78105 2.11438C3.5621 1.33333 4.81918 1.33333 7.33333 1.33333H10C11.1046 1.33333 12 2.22876 12 3.33333" stroke="white" />
                        </svg>
                        Copy
                    </button>

                    <div className="d-flex align-items-center justify-content-end gap-2">
                        <a href={`https://wa.me/?text=${encodeURIComponent(completeReferralLink)}`} target="_blank" rel="noreferrer" className="social-icon">
                            <WhatsAppIcon />
                        </a>
                        <a href={`https://www.instagram.com`} target="_blank" rel="noreferrer" className='social-icon'>
                            <InstagramIcon />
                        </a>
                        <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${completeReferralLink}&title=Join ${APP_NAME}`} target="_blank" rel="noreferrer" className='social-icon'>
                            <LinkedInIcon />
                        </a>
                        <a href={`https://x.com/intent/tweet?url=${completeReferralLink}&text=Join ${APP_NAME}`} target="_blank" rel="noreferrer" className='social-icon'>
                            <XIcon />
                        </a>
                    </div>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-center mt-3 py-5 pb-4">
                <svg width="84" height="14" viewBox="0 0 84 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line y1="6.7501" x2="30" y2="6.7501" stroke="#737373" strokeWidth="0.5" />
                    <path d="M47.1899 7.4876C44.8965 8.1051 43.1051 9.89652 42.4876 12.1899L42.0002 14.0002L41.5128 12.1899C40.8951 9.89652 39.1037 8.1051 36.8103 7.4876L35 7.0002L36.8103 6.5128C39.1037 5.8951 40.8951 4.10367 41.5126 1.81033L42 0L42.4874 1.81033C43.1049 4.10367 44.8963 5.8951 47.1897 6.5126L49 7L47.1899 7.4876Z" fill="#737373" />
                    <line x1="54" y1="6.7501" x2="84" y2="6.7501" stroke="#737373" strokeWidth="0.5" />
                </svg>
            </div>
            <div className="eligibility-section">
                <span className="eligibility-heading-sm">Eligibility</span>
                <span className="eligibility-heading">For Refferal</span>
                <div className="eligibility-card mb-5">
                    <h3 className="eligibility-card-title">To qualify for the {rewardAmount} referral bonus, make sure:</h3>
                    <div className="d-flex flex-column gap-1 mt-2">
                        <span className='eligibility-note'>1. User should connect to instagram.</span>
                        <span className='eligibility-note'>2. User should have atleast 3000 followers.</span>
                        <span className='eligibility-note'>3. Instagram account should be atleast 1 year old.</span>
                    </div>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-center pt-2 pb-4">
                <svg width="84" height="14" viewBox="0 0 84 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line y1="6.7501" x2="30" y2="6.7501" stroke="#737373" strokeWidth="0.5" />
                    <path d="M47.1899 7.4876C44.8965 8.1051 43.1051 9.89652 42.4876 12.1899L42.0002 14.0002L41.5128 12.1899C40.8951 9.89652 39.1037 8.1051 36.8103 7.4876L35 7.0002L36.8103 6.5128C39.1037 5.8951 40.8951 4.10367 41.5126 1.81033L42 0L42.4874 1.81033C43.1049 4.10367 44.8963 5.8951 47.1897 6.5126L49 7L47.1899 7.4876Z" fill="#737373" />
                    <line x1="54" y1="6.7501" x2="84" y2="6.7501" stroke="#737373" strokeWidth="0.5" />
                </svg>
            </div>
            <div className="history-section">
                <span className="history-heading">INVITE HISTORY</span>
                <div className="history-card mb-5 p-3">
                    <div className="d-flex flex-column gap-3">
                        {
                            referrals.map((referral, i) => (
                                <div className="invite-history-item" key={i}>
                                    <div className="d-flex flex-column gap-0">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className='referred-username'>{referral.referred_user_name}</span>
                                            <span className={`referral-eligibility ${referral.is_eligible ? "eligible" : "not-eligible"}`}>{referral.is_eligible ? "Eligible" : "Not Eligible"}</span>
                                        </div>
                                        <span className='referral-join-date'>Joined: {referral.created}</span>
                                    </div>
                                    {
                                        referral.ig_connect ? (
                                            <div className="d-flex gap-1 align-items-center">
                                                <InstagramIcon size={20} color="#e63030ff" />
                                                <span className='referral-username'>{referral.referred_ig_username}</span>
                                            </div>
                                        ) : (
                                            <span className='referral-not-connected'>Not Connected</span>
                                        )
                                    }
                                </div>
                            ))
                        }
                        {
                            referrals.length === 0 && (
                                <div className="py-3 d-flex align-items-center justify-content-center">
                                    <span className='text-muted'>No Referrals Yet</span>
                                </div>
                            )
                        }

                    </div>
                </div>
            </div>
        </div>
    )
}

export default ifConfigured(Referral, 'influencer_referral', '/')